import { HttpBackend, HttpClient } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {
  catchError,
  concatWith,
  defer,
  delay,
  exhaustMap,
  map,
  mergeMap,
  Observable,
  of,
  retry,
  retryWhen,
  shareReplay,
  switchMap,
  take,
  takeWhile,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import {AdminPermissionDto} from '../shared/model/admin-permission-dto';
import {FileDocumentDto} from '../shared/model/file-document-dto';
import {ProctoringDataDto} from '../shared/model/proctoring-data-dto';
import {AdminPermission, ApiResponse, UploadFile, UploadFileResponse,} from '../shared/model/response-format';
import {ChunkType} from '../shared/utils/chunk-type-enums';
import {MobileFeedDto} from '../shared/model/mobile-feed-dto';
import {AppClient} from "../../environments/client";
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ExaminationService {
  baseUrl = environment.baseUrl;
  videoFeedUrl = environment.videoFeedApi;

  deployedClient = environment.deployedClient;

  apiUrls = {
    saveImage: '/api/v1/file/save',
    saveImageWithPresignedUrl: '/api/v1/file/save-image-with-presigned-url',
    saveAdminPermissions: '/api/v1/admin-permission/save',
    getAdminPermissions: '/api/v1/drive-config/get-drive-by-driveId/',
    // saveVideoChunks: '/api/v1/file/upload-chunk',
    saveVideoChunks: '/api/v1/file/upload-chunk/init',
    saveChunkDb: '/api/v1/file/save-chunk-in-db',
    acknowledgeUploadChunks: '/api/v1/file/upload-chunk/final',
    saveAdminPersmissionWhileSubmittingTest:
      '/api/v1/user/save-proctoring-data',
    getUserDetails: '/api/v1/user/get-user-details/',
    saveProctoring: '/api/v1/proctoring/save',
    enableDisabledFocussInOrOut: '/api/v1/user/enable-or-disable-focused-in',
    getMergedFile: '/api/v1/file/get-video',
    getPreviewImage: '/api/v1/file/preview/',
    getVideoPath: '/api/v1/file/get-video-url',
    isSurroundingsVerified: '/api/v1/file/is-surroundings-verified',
    getLinkForQrCode: '/api/v1/user/mobile-feed-scanner-url/',
    mobileFeedProctoringStatus: '/api/v1/file/mobile-feed-proctoring-status',
    getMergedChunks: '/api/v1/file/merge-chunk-files/',
    generateTokenForMobile: '/api/v1/user/generate-token-for-mobile',
    getAssessmentSectionUrl:
      '/api/v1/assessment-config/get-basic-assessment-config-details/',
    getAssessmentData:
      '/api/v1/assessment/get-user-assessment-set-by-config-id/',
    getSetAllocation: '/api/v1/user/set-allocation-for-candidate/',
    // getSections: "/api/v1/assessment/start-section/",
    submitSection: '/api/v1/assessment/submit-section',
    getSectionsForProgressiveAndNonprogressive: '/api/v1/assessment/start/',
    startProgressionSection: '/api/v1/assessment/start-progressive-section',
    startNonProgressiveSection:
      '/api/v1/assessment/start-non-progressive-section/',
    saveVideoAudio: '/api/v1/file/save',
    getSubCategoriesDataForSection:
      '/api/v1/assessment/get-sub-categories-for-section',
    deleteTemporaryVideos: '/api/v1/file/delete-by-fileId',
    sendSlowInternetSpeed: '/api/v1/assessment/is-internet-slow-for-user/',
    // videoAssessmentSave: '/api/v1/file/save-video-question/',
    videoAssessmentSave: '/api/v1/file/upload-interview-video/',
    reportQuestion: '/api/v1/report-question/create',
    reportAllquestions:'/api/v1/report-question/create-all-reports',
    startQuestionBankSection: '/api/v1/assessment/start-question-bank-section/',
    getCodingLanguages: '/api/v1/assessment/get-language',
    updateCodingLanguages: '/api/v1/drive-config/',
    // getCodingQuestionOutput: "/api/v1/assessment/output-for-question",
    // audioAssessmentSave: '/api/v1/file/save-audio-question/',
    audioAssessmentSave: '/api/v1/file/upload-audio-question',
    // getPythonQuestionOutput: '/api/v1/assessment/compile-code-python',
    compileCodingQuestion: '/api/v1/assessment/compile-code',
    getSqlQuestionOutput: '/api/v1/assessment/compile-code-sql',
    saveExplainedQuestion: '/api/v1/file/save-explained-question-response',
    getMultipleBoilerPlateCode: '/api/v1/assessment/get-boilerplate-code',
    getMultipleBoilerPlateCodeForAllQuestion: '/api/v1/assessment/get-boilerplate-code-for-all-questions',
    createCodeServer: '/api/v1/assessment/create-code-server',
    createJupyterServer: '/api/v1/assessment/create-jupyter-server',
    // createCodeServer: 'http://localhost:9876/api/containers/launch',
    containerStatusCheck: '/api/v1/assessment/status',
    stopCodeServer: '/api/v1/assessment/stop-code-server',
    createCodeServerDrive: '/api/v1/assessment/create-code-server-drive',
    // stopCodeServer: 'http://localhost:9876/api/containers',
    verifyInviteLink: '/api/v1/drive-config/verify-invite-link',
    saveTechnicalQuestion:
      '/api/v1/assessment/save-candidate-technical-questions',
    triggerWorkday: '/api/v1/drive-config/trigger-workday',
    openAIChatCompletion: '/api/v1/assessment/completions',
    stopJupyterServer: '/api/v1/assessment/stop-jupyter-server',
    runIDETestCases: '/api/v1/assessment/process-candidate-ide-testcases',
    resumeParseInterviewBot:
      'https://pixiproductmasterstaging.foyr.com/api/autodeploymentai/resumeParseforInterviwBot',
    driveInterviewConfig: '/api/v1/drive-config/get-drive-settings/',
    getInterviewQuestions: '/api/v1/assessment/get-interview-questions',
    updatePreviewPort: '/api/v1/assessment/change-preview-port',
    saveBotInterviewResponse:
      '/api/v1/assessment/save-candidate-interview-response',
    createCandidateInterviewSession:
      '/api/v1/assessment/create-candidate-interview-session',
    saveScreeningResponses:
      '/api/v1/drive-config/save-screening-responses',
    interviewCallStatus: '/api/v1/status/',
    // interviewCallStatus: 'https://fractalvapi.foyr.com/status/',
    uploadVerifySurrounding: '/api/v1/file/upload-verify-surrounding-video/',
    //interviewCallStatus: 'https://fractalvapi.foyr.com/status/',
    getSqlQuestionFile: '/api/v1/assessment/download-sqlite',
    getDriveStatus: '/api/v1/assessment/get-drive-status/',
    getOfflineNonProgressiveSection:
      '/api/v1/assessment/start-non-progressive-section/offline/',
    getOfflineProgressiveSection:
      '/api/v1/assessment/start-progressive-section/offline/',
    submitAllSections: '/api/v1/assessment/submit-all-sections',
    submitAdditionalEvents: '/api/v1/assessment/additional-events',
    getOfflineQuestionBankSection:
      '/api/v1/assessment/start-question-bank-section/offline/',
    endAssessmentStatus: '/api/v1/assessment/update-status-for-all-processes',
    uploadPopupQuestionVideo: '/api/v1/file/upload-popup-question-video',
    submitSectionPayload: '/api/v1/assessment/submit-section-payload-dump',
    generateResumeQuestion: '/api/v1/practice-bot/generate-resume-questions/',
    scenarioBasedQuestion: '/api/v1/practice-bot/scenario-based/',
    setInterviewStatus: '/api/v1/assessment/set-interview-status/',
    callWebEvent: '/call/web/event',
    resetMobileScannerStatusForBot :'/api/v1/drive-config/initiate-interview-bot-drive'
  };
  constructor(
    private _http: HttpClient,
    private http: HttpClient,
    private handler: HttpBackend
  ) {
    this.cleanHttp = new HttpClient(handler);
  }
  mobileFeedDto: MobileFeedDto = new MobileFeedDto();
  timeoutDuration = 60000;
  // saveCaptureImage(
  //   assessmentDriveSetId,
  //   requestPayload: FormData
  // ): Observable<ApiResponse<FileDocumentDto>> {
  //   return this._http.post<ApiResponse<FileDocumentDto>>(
  //     this.baseUrl + this.apiUrls.saveImage + '/' + assessmentDriveSetId,
  //     requestPayload
  //   );
  // }


  private cleanHttp: HttpClient;
  private geo$?: Observable<any>;

  saveCaptureImage(
    requestPayload: any,
    assessmentDriveSetId: string,
  ): Observable<ApiResponse<any>> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.saveImageWithPresignedUrl + '/' + assessmentDriveSetId,
    )
      .pipe(
        timeout(this.timeoutDuration),
        retry(2),
        switchMap((res) => {

          const uploadUrl = res.data?.uploadUrl;
          const fileId = res.data?.Key;

          if (!uploadUrl || !fileId) {
            throw new Error('UploadUrl or FileId missing in response');
          }

          // Upload the video file to the pre-signed URL
          return this._http.put(uploadUrl, requestPayload, {
            observe: 'response',
          }).pipe(
            retry(2),
            map(() => ({
              statusCode: 200,
              message: 'Video uploaded successfully',
              data: { fileId },
            }) as ApiResponse<any>),
          );
        })
      );
  }

  saveProfileImage(
    requestPayload: FormData
  ): Observable<ApiResponse<FileDocumentDto>> {
    return this._http.post<ApiResponse<FileDocumentDto>>(
      this.baseUrl + this.apiUrls.saveImage,
      requestPayload
    );
  }

  saveAdminPermissions(
    requestPayload: AdminPermission
  ): Observable<ApiResponse<AdminPermission>> {
    return this._http.post<ApiResponse<AdminPermission>>(
      this.baseUrl + this.apiUrls.saveAdminPermissions,
      requestPayload
    );
  }

  getAdminPermissions(driveId): Observable<ApiResponse<AdminPermissionDto>> {
    return this._http.get<ApiResponse<AdminPermissionDto>>(
      this.baseUrl + this.apiUrls.getAdminPermissions + driveId
    );
  }
  saveAdminPermissionsForTest(
    requestPayload: ProctoringDataDto
  ): Observable<ApiResponse<ProctoringDataDto>> {
    return this._http.post<ApiResponse<ProctoringDataDto>>(
      this.baseUrl + this.apiUrls.saveAdminPersmissionWhileSubmittingTest,
      requestPayload
    );
  }
  // saveChunkVideos(
  //   requestPayload: UploadFile,
  //   sequence: number,
  //   ChunkType: ChunkType
  // ): Observable<ApiResponse<UploadFile>> {
  //   let assessmentDriveSetId = sessionStorage.getItem('assessmentDriveSetId');
  //   return this._http
  //     .post<ApiResponse<UploadFile>>(
  //       this.baseUrl +
  //         this.apiUrls.saveVideoChunks +
  //         '/' +
  //         sequence +
  //         '/' +
  //         ChunkType +
  //         '/' +
  //         assessmentDriveSetId,
  //       requestPayload
  //     )
  //     .pipe(timeout(this.timeoutDuration));
  // }

  // saveChunkVideos(
  //   requestPayload: UploadFile,
  //   sequence: number,
  //   chunkType: ChunkType,
  //   fileSize: number,
  //   contentType: string
  // ): Observable<any> {
  //   const assessmentDriveSetId = sessionStorage.getItem('assessmentDriveSetId');
  //   let websocketSessionId = localStorage.getItem('websocketSessionId');

  //   return  this.http
  //     .post<ApiResponse<UploadFileResponse>>(
  //       this.baseUrl + this.apiUrls.saveVideoChunks,
  //       {
  //         sequence,
  //         chunkType,
  //         assessmentDriveSetId,
  //         fileSize,
  //         contentType,
  //         websocketSessionId
  //       }

  //     )
  //     .pipe(
  //       timeout(this.timeoutDuration),
  //       switchMap((res) => {
  //         const uploadUrl = res.data?.uploadUrl;
  //         const fileId = res.data?.fileId;

  //         if (!uploadUrl || !fileId) {
  //           throw new Error('UploadUrl or FileId missing in response');
  //         }
  //         return this._http.put(uploadUrl, requestPayload, {
  //           observe: 'response',
  //         });
  //         // .pipe(
  //         //   switchMap(() => {
  //         //     return this._http.post<ApiResponse<any>>(
  //         //       this.baseUrl + this.apiUrls.acknowledgeUploadChunks + `?fileId=${fileId}&isSuccess=true`,
  //         //       {}
  //         //     );
  //         //   })
  //         // );
  //       })
  //     );
  // }

  saveChunkVideos(
    requestPayload: UploadFile,
    sequence: number,
    chunkType: ChunkType,
    fileSize: number,
    contentType: string
  ): Observable<any> {
    const assessmentDriveSetId = sessionStorage.getItem('assessmentDriveSetId');
    const userAssessmentDriveSetId = sessionStorage.getItem(
      'userAssessmentDriveSetId'
    );
    const websocketSessionId = localStorage?.getItem('websocketSessionId');
    const isInterviewBotDrive =
      sessionStorage.getItem('isInterviewBotDrive') === 'true';

    return this.http
      .post<ApiResponse<UploadFileResponse>>(
        this.baseUrl + this.apiUrls.saveVideoChunks,
        {
          sequence,
          chunkType,
          assessmentDriveSetId,
          fileSize,
          contentType,
          userAssessmentDriveSetId,
          ...(isInterviewBotDrive && {
            userInterviewSessionId: sessionStorage.getItem(
              'userInterviewSessionId'
            ),
          }),
        },
        {
          headers: { 'X-Skip-Error-Interceptor': 'true' }
        }
      )
      .pipe(
        timeout(this.timeoutDuration),
        retry(2),
        switchMap((res) => {
          if (res?.data?.isAssessmentSubmitted) {
            return of(res);
          }

          const uploadUrl = res.data?.uploadUrl;
          const fileId = res.data?.fileId;

          if (!uploadUrl || !fileId) {
            throw new Error('UploadUrl or FileId missing in response');
          }
          return this._http
            .put(uploadUrl, requestPayload, {
              observe: 'response',
            })
          .pipe(
          retry(2),
          catchError((err)=>this.callFallbackMongodbForFeedChunks(
            requestPayload,
            sequence,
            chunkType,
            websocketSessionId,
            assessmentDriveSetId,
            userAssessmentDriveSetId
          ))
          )
        }),
        catchError((err) => this.callFallbackMongodbForFeedChunks(
            requestPayload,
            sequence,
            chunkType,
            websocketSessionId,
            assessmentDriveSetId,
            userAssessmentDriveSetId
          )
        )
      )
  }
  saveChunkInDb(formData: FormData): Observable<any> {
    return this.http.post(
      this.baseUrl + this.apiUrls.saveChunkDb,
      formData,
        {
          headers: { 'X-Skip-Error-Interceptor': 'true' }
        }
    );
  }


  videoSaveAssessment(
    requestPayload: any,
    userAssessmentDriveSetId: string,
    sectionQuestionId: string
  ): Observable<ApiResponse<any>> {

    const websocketSessionId = localStorage.getItem('websocketSessionId');
    const assessmentDriveSetId = sessionStorage.getItem('assessmentDriveSetId');

    return this._http
      .get<ApiResponse<any>>(
        `${this.baseUrl}${this.apiUrls.videoAssessmentSave}${userAssessmentDriveSetId}/${sectionQuestionId}`,
        {
          headers: { 'X-Skip-Error-Interceptor': 'true' }
        }
      )
      .pipe(
        timeout(this.timeoutDuration),
        retry(2),

        switchMap((res) => {
          const uploadUrl = res.data?.uploadUrl;
          const fileId = res.data?.fileId;

          // if (!uploadUrl || !fileId) {
          //   throw new Error('UploadUrl or FileId missing in response');
          // }

          return this._http
            .put(uploadUrl, requestPayload, { observe: 'response' })
            .pipe(
              retry(2),

              map(() => ({
                statusCode: 200,
                message: 'Video uploaded successfully',
                data: { fileId },
              }) as ApiResponse<any>),

              // If PUT fails → fallback BUT keep fileId
              catchError(() =>
                this.callFallbackMongodbForVideoQuestion(
                  requestPayload,
                  sectionQuestionId,
                  websocketSessionId,
                  assessmentDriveSetId,
                  userAssessmentDriveSetId,
                ).pipe(
                  map(() => ({
                    statusCode: 200,
                    message: 'Video upload failed, fallback used',
                    data: { fileId },
                  }))
                )
              )
            );
        }),

        // If FIRST API fails → fallback
        catchError(() =>
          this.callFallbackMongodbForVideoQuestion(
            requestPayload,
            sectionQuestionId,
            websocketSessionId,
            assessmentDriveSetId,
            userAssessmentDriveSetId
          )
        )
      );
  }


  audioSaveAssessment(
    requestPayload: any,
    userAssessmentDriveSetId: string,
    sectionQuestionId: string
  ): Observable<ApiResponse<any>> {
    let websocketSessionId = localStorage.getItem('websocketSessionId');
    const assessmentDriveSetId = sessionStorage.getItem('assessmentDriveSetId');
    return this._http
      .get<ApiResponse<any>>(
        `${this.baseUrl}${this.apiUrls.audioAssessmentSave}/${userAssessmentDriveSetId}/${sectionQuestionId}`,
        {
          headers: { 'X-Skip-Error-Interceptor': 'true' }
        })
      .pipe(
        timeout(this.timeoutDuration),
        retry(2),
        switchMap((res) => {
          console.log(res,"response");

          const uploadUrl = res.data?.uploadUrl;
          const fileId = res.data?.fileKey;

          // if (!uploadUrl || !fileId) {
          //   throw new Error('UploadUrl or FileId missing in response');
          // }

          return this._http
            .put(uploadUrl, requestPayload, {
              observe: 'response',
            })
            .pipe(
              retry(2),
              map(
                () =>
                  ({
                    statusCode: 200,
                    message: 'Audio uploaded successfully',
                    data: { fileId },
                  } as ApiResponse<any>)
              ),
              catchError((err)=> this.callFallbackMongodbForAudioQuestion(
                requestPayload,
                sectionQuestionId,
                websocketSessionId,
                assessmentDriveSetId,
                userAssessmentDriveSetId
              ).pipe(
                map(() => ({
                  statusCode: 200,
                  message: 'Audio s3 upload failed, fallback used',
                  data: { fileId },
                }))
              )
            )
            );
        }),
        catchError((err)=> this.callFallbackMongodbForAudioQuestion(
          requestPayload,
          sectionQuestionId,
          websocketSessionId,
          assessmentDriveSetId,
          userAssessmentDriveSetId
        ))
      );
  }

  // audioSaveAssessment(
  //   requestPayload,
  //   userAssessmentDriveSetId,
  //   questionId
  // ): Observable<ApiResponse<any>> {
  //   return this._http.post<ApiResponse<any>>(
  //     this.baseUrl +
  //       this.apiUrls.audioAssessmentSave +
  //       userAssessmentDriveSetId +
  //       '/' +
  //       questionId,
  //     requestPayload
  //   );
  // }

  getUserDetails(requestPayload): Observable<ApiResponse<ProctoringDataDto>> {
    return this._http.get<ApiResponse<ProctoringDataDto>>(
      this.baseUrl + this.apiUrls.getUserDetails + requestPayload
    );
  }
  saveProctoring(
    requestPayload: string,
    boolean
  ): Observable<ApiResponse<Map<string, boolean>>> {
    return this._http.post<ApiResponse<Map<string, boolean>>>(
      this.baseUrl + this.apiUrls.saveProctoring,
      requestPayload
    );
  }
  enableOrDisableFocussedInOrOut(
    requestPayload: any
  ): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.enableDisabledFocussInOrOut,
      requestPayload
    );
  }
  getStoredVideoOfExamination(/*studentName:string*/): Observable<
    ApiResponse<FileDocumentDto[]>
  > {
    return this._http.get<ApiResponse<FileDocumentDto[]>>(
      this.baseUrl + this.apiUrls.getMergedFile /*+'/'+studentName*/
    );
  }
  getVideoPathOfExamination(/*studentName:string*/): Observable<
    ApiResponse<string>
  > {
    return this._http.get<ApiResponse<string>>(
      this.baseUrl + this.apiUrls.getVideoPath
    );
  }
  getMergeChunksFilesExamination(): Observable<ApiResponse<string>> {
    let assessmentDriveSetId = sessionStorage.getItem('assessmentDriveSetId');
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoFeedUrl;
    }
    return this._http
      .get<ApiResponse<string>>(
        url + this.apiUrls.getMergedChunks + assessmentDriveSetId
      )
      .pipe(timeout(this.timeoutDuration));
  }

  getImage(fileId: string): Observable<ApiResponse<FileDocumentDto>> {
    return this._http.get<ApiResponse<FileDocumentDto>>(
      this.baseUrl + this.apiUrls.getPreviewImage + fileId
    );
  }

  getLocationAndIpAddress(): Observable<any> {
    if (!this.geo$) {
      this.geo$ = this.cleanHttp
        .get('https://geolocation-db.com/json/')
        .pipe(shareReplay(1));
    }
    return this.geo$;
  }

  getSurroundingVerified(): Observable<ApiResponse<boolean>> {
    return this._http.get<ApiResponse<boolean>>(
      this.baseUrl + this.apiUrls.isSurroundingsVerified
    );
  }

  getQrCodeLink(driveId): Observable<any> {
    return this._http.get(
      this.baseUrl + this.apiUrls.getLinkForQrCode + driveId
    );
  }

  getMobileFeedProctoringStatus(
    mobileFeeStatus?: boolean
  ): Observable<ApiResponse<MobileFeedDto>> {
    let assessmentDriveSetId = sessionStorage.getItem('assessmentDriveSetId');
    if (mobileFeeStatus) {
      return this._http.get<ApiResponse<MobileFeedDto>>(
        this.baseUrl +
          this.apiUrls.mobileFeedProctoringStatus +
          '/' +
          assessmentDriveSetId +
          '?mobileFeedCaptured=true'
      );
    } else {
      return this._http.get<ApiResponse<MobileFeedDto>>(
        this.baseUrl +
          this.apiUrls.mobileFeedProctoringStatus +
          '/' +
          assessmentDriveSetId +
          '?mobileFeedCaptured=false'
      );
    }
  }

  generateTokenForMobile(
    payload:{mobileNumber: string}
  ): Observable<ApiResponse<MobileFeedDto>> {
    return this._http.post<ApiResponse<MobileFeedDto>>(
      this.baseUrl + this.apiUrls.generateTokenForMobile ,payload
    );
  }
  getAssessmentSections(
    assissmentConfigId,
    assessmentDriveSetId?: string | null
  ): Observable<ApiResponse<any>> {
    // assessmentDriveSetId is an optional query param: the backend uses it to
    const setId =
      assessmentDriveSetId ?? sessionStorage.getItem('assessmentDriveSetId');
    const hasSetId = !!setId && setId !== 'null' && setId !== 'undefined';
    let url =
      this.baseUrl + this.apiUrls.getAssessmentSectionUrl + assissmentConfigId;
    if (hasSetId) {
      url += `?assessmentDriveSetId=${setId}`;
    }
    return this._http.get<ApiResponse<any>>(url)
  }

  getUserAssessmentData(assissmentConfigId): Observable<ApiResponse<any>> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getAssessmentData + assissmentConfigId
    )
  }

  getSetAllocationDetails(assissmentConfigId): Observable<ApiResponse<any>> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getSetAllocation + assissmentConfigId + `?isOnlineMode=${!environment.DESKTOP_IQIGAI_LITE}`
    )
    .pipe(
      retryWhen(errors =>
        errors.pipe(
          mergeMap((error, retryCount) => {
            if (retryCount >= 2) {
              return throwError(() => error);
            }
            return new Promise(res => setTimeout(res, 3000));
          })
        )
      )
    );
  }

pollNonProgressiveSection(assissmentConfigId: string) {
  return timer(0, 3000).pipe(
    exhaustMap(() =>
      this.getSectionNonProgressiveQuestions(assissmentConfigId).pipe(
        catchError(err => of({ statusCode: err.status || 400 }))
      )
    ),
    takeWhile((res: any) => res?.statusCode !== 200, true),
    take(4)
  );
}

  getSectionNonProgressiveQuestions(
    assissmentConfigId
  ): Observable<ApiResponse<any>> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl +
        this.apiUrls.startNonProgressiveSection +
        assissmentConfigId
    )
  }

  getQuestionBankSectionQuestions(
    assessmentConfigId
  ): Observable<ApiResponse<any>> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.startQuestionBankSection + assessmentConfigId
    );
  }

  getSectionProgressiveQuestions(requestPayload): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.startProgressionSection,
      requestPayload,
      {
        headers: { 'X-Skip-Error-Interceptor': 'true' }
      }
    )
    .pipe(
      retryWhen(errors =>
        errors.pipe(
          mergeMap((error, retryCount) => {
            if (retryCount >= 2) {
              return throwError(() => error);
            }
            return new Promise(res => setTimeout(res, 3000));
          })
        )
      )
    );
  }

  // getSectionProgressiveOrNonProgrossive(
  //   assissmentConfigId
  // ): Observable<ApiResponse<any>> {
  //   return this._http.get<ApiResponse<any>>(
  //     this.baseUrl +
  //       this.apiUrls.getSectionsForProgressiveAndNonprogressive +
  //       assissmentConfigId
  //   )
  // }


  getSectionProgressiveOrNonProgrossive(
    assissmentConfigId: string
  ): Observable<ApiResponse<any>> {
    return defer(() =>
      this._http.get<ApiResponse<any>>(
        this.baseUrl +
          this.apiUrls.getSectionsForProgressiveAndNonprogressive +
          assissmentConfigId
      )
    ).pipe(
      map((res) => {
        const data = res?.data;

        const isAllNull =
          data?.isNonProgressiveSection == null &&
          data?.isOfflineAssessment == null &&
          data?.isProgressiveSection == null &&
          data?.isQuestionBankSection == null &&
          data?.sectionSequence == null;

        if (isAllNull) {
          throw new Error('All values are null');
        }

        // ✅ valid response
        return res;
      }),

      retryWhen((errors) =>
        errors.pipe(
          take(5),

          delay(2000),

          concatWith(
            throwError(() => new Error('Max retries reached'))
          )
        )
      )
    );
  }




  getSubCategoriesDataForSection(requestPayload): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getSubCategoriesDataForSection,
      requestPayload
    );
  }

  submitSections(requestPayload): Observable<ApiResponse<any>> {
    return this._http
      .post<ApiResponse<any>>(
        this.baseUrl + this.apiUrls.submitSection,
        requestPayload,
        {
          headers: { 'X-Skip-Error-Interceptor': 'true' }
        }
      )
      .pipe(
        retryWhen(errors =>
          errors.pipe(
            mergeMap((error, retryCount) => {
              if (retryCount >= 2) {
                return throwError(() => error);
              }
              return new Promise(res => setTimeout(res, 3000));
            })
          )
        )
      );
  }

  saveVideoAudioChunks(requestPayload): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.saveVideoAudio,
      requestPayload
    );
  }

  deleteTemporaryVideos(previousIds: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.deleteTemporaryVideos,
      previousIds
    );
  }

  sendSlowInternetSpeed(
    isInternetSlow: boolean,
    driveId: any
  ): Observable<ApiResponse<any>> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl +
        this.apiUrls.sendSlowInternetSpeed +
        isInternetSlow +
        '/' +
        driveId
    );
  }

  reportWrongQuestion(QuestionReportDto: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.reportQuestion,
      QuestionReportDto
    );
  }
  reportAllWrongQuestions(QuestionReportDto:any):Observable<any>{
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.reportAllquestions,
      QuestionReportDto,
      {
        headers: { 'X-Skip-Error-Interceptor': 'true' }
      }
    ).pipe(retry(2));
  }

  getCodingLanguages(): Observable<any> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getCodingLanguages
    );
  }
  getCodingLanguagesByDriveId(driveId:any): Observable<any> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getCodingLanguages + '/' + driveId
    );
  }
  updateCodingLanguagesByDriveId(driveId: any, codingLanguages: { id: string; name: string }[]): Observable<any> {
    return this._http.put<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.updateCodingLanguages + driveId + '/coding-languages',
      { codingLanguages }
    );
  }

  getCodingQuestionOutput(codingQuestionDto: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.compileCodingQuestion,
      codingQuestionDto,
      {
        headers: { 'X-Skip-Error-Interceptor': 'true' }
      }
    )
    .pipe(
      retryWhen(errors =>
        errors.pipe(
          mergeMap((error, retryCount) => {
            if (retryCount >= 2) {
              return throwError(() => error);
            }
            return new Promise(res => setTimeout(res, 3000));
          })
        )
      )
    );
  }

  // getPythonQuestionOutput(codingQuestionDto: any): Observable<any> {
  //   return this._http.post<ApiResponse<any>>(
  //     this.baseUrl + this.apiUrls.getPythonQuestionOutput,
  //     codingQuestionDto
  //   );
  // }

  getSqlQuestionOutput(codingQuestionDto: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getSqlQuestionOutput,
      codingQuestionDto,
      {
        headers: { 'X-Skip-Error-Interceptor': 'true' }
      }
    )
    .pipe(
      retryWhen(errors =>
        errors.pipe(
          mergeMap((error, retryCount) => {
            if (retryCount >= 2) {
              return throwError(() => error);
            }
            return new Promise(res => setTimeout(res, 3000));
          })
        )
      )
    );
  }

  createCodeServer(requestPayload: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.createCodeServer,
      requestPayload
    );
  }

   createJupyterServer(requestPayload: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.createJupyterServer,
      requestPayload
    );
  }

  createCodeServerDrive(requestPayload: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.createCodeServerDrive,
      requestPayload
    );
  }

  // createCodeServer(requestPayload: any): Observable<any> {
  //   return this._http.post<ApiResponse<any>>(
  //     this.apiUrls.createCodeServer,
  //     requestPayload
  //   );
  // }

  containerStatusCheck(questionId: any): Observable<any> {
    return this._http.get<ApiResponse<any>>(
       this.baseUrl + this.apiUrls.containerStatusCheck + '/' + questionId
    );
  }

  stopCodeServer(requestPayload: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.stopCodeServer,
      requestPayload
    );
  }

  callWebEvent(requestPayload: any): Observable<any> {
    return this._http.post<any>(
      this.baseUrl + this.apiUrls.callWebEvent,
      requestPayload
    );
  }

  // stopCodeServer(requestPayload: any): Observable<any> {
  //   return this._http.delete<ApiResponse<any>>(
  //     this.apiUrls.stopCodeServer + '/' + requestPayload.questionId,
  //     requestPayload
  //   );
  // }

  saveExplainedQuestion(
    userAssessmentDriveSetId: string,
    sectionId: string,
    questionId: string,
    isAudioType: boolean,
    file: File
  ): Observable<any> {
    let assessmentDriveSetId = sessionStorage.getItem('assessmentDriveSetId');
    let websocketSessionId = localStorage.getItem('websocketSessionId');
    return this._http
      .post<ApiResponse<any>>(
        this.baseUrl +
          this.apiUrls.uploadPopupQuestionVideo +
          `?userAssessmentDriveSetId=${userAssessmentDriveSetId}&sectionId=${sectionId}&questionId=${questionId}&isAudioType=${isAudioType}`,
        {
          headers: { 'X-Skip-Error-Interceptor': 'true' }
        },
        {}
      )
      .pipe(
        timeout(this.timeoutDuration),
        retry(2),
        switchMap((res) => {
          const uploadUrl = res.data?.uploadUrl;
          const fileId = res.data?.fileId;

          // if (!uploadUrl || !fileId) {
          //   throw new Error('UploadUrl or FileId missing in response');
          // }
          return this._http
            .put(uploadUrl, file, {
              observe: 'response',
            })
            .pipe(retry(2),
             map(() => ({
                statusCode: 200,
                message: 'Video explainer uploaded successfully',
                data: { fileId },
              }) as ApiResponse<any>),
            catchError((err)=> this.callFallbackMongodbForExplainedVideoQuestion(
              file,
              sectionId,
              questionId,
              websocketSessionId,
              assessmentDriveSetId,
              userAssessmentDriveSetId))
            );
        }),
        catchError((err)=> this.callFallbackMongodbForExplainedVideoQuestion(
          file,
          sectionId,
          questionId,
          websocketSessionId,
          assessmentDriveSetId,
          userAssessmentDriveSetId))
        );
  }

  getMultipleBoilerPlateCode(requestPayload): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getMultipleBoilerPlateCode,
      requestPayload,
      {
        headers: { 'X-Skip-Error-Interceptor': 'true' }
      }
    )
    .pipe(
      retryWhen(errors =>
        errors.pipe(
          mergeMap((error, retryCount) => {
            if (retryCount >= 2) {
              return throwError(() => error);
            }
            return new Promise(res => setTimeout(res, 3000));
          })
        )
      )
    );
  }
  getMultipleBoilerPlateCodeForAllQuestion(questionIds): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getMultipleBoilerPlateCodeForAllQuestion,
      questionIds,
      {
        headers: { 'X-Skip-Error-Interceptor': 'true' }
      }
    )
    .pipe(
      retryWhen(errors =>
        errors.pipe(
          mergeMap((error, retryCount) => {
            if (retryCount >= 2) {
              return throwError(() => error);
            }
            return new Promise(res => setTimeout(res, 3000));
          })
        )
      )
    );
  }

  verifyInviteLink(token: string): Observable<ApiResponse<any>> {
    const encodedToken = encodeURIComponent(token);
    const url = `${this.baseUrl}${this.apiUrls.verifyInviteLink}?token=${encodedToken}`;
    return this._http.get<ApiResponse<any>>(url);
  }

  saveTechnicalQuestion(requestPayload): Observable<any> {
    return this._http.post<any>(
      this.baseUrl + this.apiUrls.saveTechnicalQuestion,
      requestPayload
    );
  }

  triggerWorkday(
    userAssessmentDriveSetId: string,
    token: string
  ): Observable<ApiResponse<any>> {
    const url = `${this.baseUrl}${
      this.apiUrls.triggerWorkday
    }?userAssessmentDriveSetId=${encodeURIComponent(
      userAssessmentDriveSetId
    )}&token=${encodeURIComponent(token)}`;
    return this._http.get<ApiResponse<any>>(url);
  }

  openAIChatCompletion(requestPayload): Observable<any> {
    return this._http.post<any>(
      this.baseUrl + this.apiUrls.openAIChatCompletion,
      requestPayload
    );
  }

  stopJupyterServer(containerName): Observable<ApiResponse<any>> {
    return this._http.delete<ApiResponse<any>>(
      this.baseUrl +
        this.apiUrls.stopJupyterServer +
        '?containerName=' +
        containerName
    );
  }

  runIDETestCases(requestPayload): Observable<any> {
    return this._http.post<any>(
      this.baseUrl + this.apiUrls.runIDETestCases,
      requestPayload
    );
  }

  // Vapi Bot Apis
  resumeParseInterviewBot(requestPayload): Observable<any> {
    return this._http.post<any>(
      this.apiUrls.resumeParseInterviewBot,
      requestPayload
    );
  }

  updatePreviewPort(requestPayload): Observable<any> {
    return this._http.post<any>(
      this.baseUrl + this.apiUrls.updatePreviewPort,
      requestPayload
    );
  }

  driveInterviewConfig(driveId): Observable<any> {
    return this._http.get<any>(
      this.baseUrl + this.apiUrls.driveInterviewConfig + driveId
    );
  }

  saveScreeningResponses(requestPayload): Observable<any> {
    return this._http.post<any>(
      this.baseUrl + this.apiUrls.saveScreeningResponses,
      requestPayload
    );
  }

  getInterviewQuestions(requestPayload): Observable<any> {
    return this._http.post<any>(
      this.baseUrl +
        this.apiUrls.getInterviewQuestions,
        requestPayload
    );
  }

  saveBotInterviewResponse(requestPayload): Observable<any> {
    return this._http.post<any>(
      this.baseUrl + this.apiUrls.saveBotInterviewResponse,
      requestPayload
    );
  }

  createCandidateInterviewSession(requestPayload): Observable<any> {
    return this._http.post<any>(
      this.baseUrl + this.apiUrls.createCandidateInterviewSession,
      requestPayload
    );
  }

  uploadVerifySurroundingVideo(
    requestPayload: UploadFile,
    sequence: number,
    ChunkType: ChunkType
  ): Observable<any> {
    let assessmentDriveSetId = sessionStorage.getItem('assessmentDriveSetId');
    let userAssessmentDriveSetId = sessionStorage.getItem(
      'userAssessmentDriveSetId'
    );
    let websocketSessionId = localStorage.getItem('websocketSessionId');
    const isInterviewBotDrive =
      sessionStorage.getItem('isInterviewBotDrive') === 'true';
    return this._http
      .post<ApiResponse<UploadFileResponse>>(
        this.baseUrl +
          this.apiUrls.uploadVerifySurrounding +
          sequence +
          '/' +
          ChunkType +
          '/' +
          assessmentDriveSetId +
          '/' +
          userAssessmentDriveSetId,
        isInterviewBotDrive
          ? {
              userInterviewSessionId: sessionStorage.getItem(
                'userInterviewSessionId'
              ),
            }
          : {},
        {
          headers: { 'X-Skip-Error-Interceptor': 'true' }
        }
      )
      .pipe(
        timeout(this.timeoutDuration),
        retry(2),
        switchMap((res) => {
          const uploadUrl = res.data?.uploadUrl;

          // if (!uploadUrl || !fileId) {
          //   throw new Error('UploadUrl or FileId missing in response');
          // }
          return this._http
            .put(uploadUrl, requestPayload, {
              observe: 'response',
            })
            .pipe(retry(2),
            catchError((err)=>
              this.callFallbackMongodbForVerifySurroundingVideo(
                requestPayload,
                sequence,
                ChunkType,
                websocketSessionId,
                assessmentDriveSetId,
                userAssessmentDriveSetId
              )
            ));
        }),
        catchError((err)=>
          this.callFallbackMongodbForVerifySurroundingVideo(
            requestPayload,
            sequence,
            ChunkType,
            websocketSessionId,
            assessmentDriveSetId,
            userAssessmentDriveSetId
          )
        ));
  }

  interviewCallStatus(callId): Observable<any> {
    //return this._http.get<any>(this.apiUrls.interviewCallStatus + callId);
    return this._http.get<any>(this.baseUrl + this.apiUrls.interviewCallStatus + callId);
  }
  getSqlQuestionFile(questionIds: string[]): Observable<ArrayBuffer> {
    return this._http.post(
      this.baseUrl + this.apiUrls.getSqlQuestionFile,
      questionIds,
      { responseType: 'arraybuffer' }
    );
  }

  getDriveStatus(driveId): Observable<any> {
    return this._http.get<any>(
      this.baseUrl + this.apiUrls.getDriveStatus + driveId + `?isOnlineMode=${!environment.DESKTOP_IQIGAI_LITE}`
    );
  }

  getOfflineNonProgressiveSection(driveId): Observable<any> {
    return this._http.get<any>(
      this.baseUrl + this.apiUrls.getOfflineNonProgressiveSection + driveId
    )
    .pipe(
      retryWhen(errors =>
        errors.pipe(
          delay(3000),
          take(2)
        )
      )
    );
  }

  getOfflineProgressiveSection(driveId): Observable<any> {
    return this._http.post<any>(
      this.baseUrl + this.apiUrls.getOfflineProgressiveSection + driveId ,{}
    );
  }

  // submitAllSections(requestPayload): Observable<ApiResponse<any>> {
  //   return this._http.post<ApiResponse<any>>(
  //     this.baseUrl + this.apiUrls.submitAllSections,
  //     requestPayload
  //   )
  //   .pipe(retry(2));
  // }

  submitAllSections(requestPayload): Observable<ApiResponse<any>> {
    return this._http
      .post<ApiResponse<any>>(
        this.baseUrl + this.apiUrls.submitAllSections,
        requestPayload
      )
      .pipe(
        retryWhen((errors) =>
          errors.pipe(
            delay(5000),
            takeWhile((_, index) => index < 12) // Retry for 5 min (60 * 5s)
          )
        ),
        catchError((error) => {
          console.error('API request failed after retries', error);

          // 👇 Return a proper ApiResponse object
          const errorResponse = {
            status: 'error',
            data: null,
            statusCode: error.status ?? 500,
            value: null,
            msg: 'API request failed after multiple retries',
          } as unknown as ApiResponse<any>;

          return of(errorResponse);
        })
      );
  }

  /**
   * Real reachability probe. `navigator.onLine` can report offline while the
   * network actually works (VPNs, captive portals, virtual adapters). This HEAD
   * probe resolves true if the API host is reachable and false only on a genuine
   * network error, so submission polling isn't blocked by a false-negative flag.
   */
  async checkConnectivity(timeoutMs = 4000): Promise<boolean> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      await fetch(this.baseUrl, {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-store',
        signal: controller.signal,
      });
      return true;
    } catch {
      return false;
    } finally {
      clearTimeout(timer);
    }
  }

  submitAdditionalEvents(requestPayload): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.submitAdditionalEvents,
      requestPayload
    );
  }

  getOfflineQuestionBankSection(driveId): Observable<any> {
    return this._http.get<any>(
      this.baseUrl + this.apiUrls.getOfflineQuestionBankSection + driveId
    );
  }

  endAssessmentStatus(userAssessmentDriveSetId): Observable<ApiResponse<any>> {
    return this._http.put<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.endAssessmentStatus,
      {
        userAssessmentDriveSetId,
      },
      {
        headers: { 'X-Skip-Error-Interceptor': 'true' }
      }
    )
    .pipe(
      retryWhen(errors =>
        errors.pipe(
          delay(3000),
          take(2)
        )
      )
    );
  }

  submitSectionsPayload(requestPayload): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.submitSectionPayload,
      requestPayload
    );
  }
  
    setInterviewStatus(callId: string): Observable<any> {
    return this._http.get<any>(
      this.baseUrl + this.apiUrls.setInterviewStatus + callId
    );
  }

  callFallbackMongodbForFeedChunks(
    requestPayload: UploadFile,
    sequence: number,
    chunkType: ChunkType,
    websocketSessionId: string | null,
    assessmentDriveSetId: string | null,
    userAssessmentDriveSetId: string | null
  ): Observable<any> {
    const formData = new FormData();

    formData.append('file', requestPayload as File);
    formData.append(
      'data',
      new Blob(
        [
          JSON.stringify({
            sequence,
            isChunkUpload: true,
            chunkType,
            sessionId: websocketSessionId,
            assessmentDriveSetId,
            userAssessmentDriveSetId,
          }),
        ],
        { type: 'application/json' }
      )
    );

    return this.saveChunkInDb(formData).pipe(retry(2));
  }

  callFallbackMongodbForVideoQuestion(
    requestPayload: UploadFile,
    sectionQuestionId: string,
    websocketSessionId: string | null,
    assessmentDriveSetId: string | null,
    userAssessmentDriveSetId: string | null
  ): Observable<any> {
    const formData = new FormData();

    formData.append('file', requestPayload as File);
    formData.append(
      'data',
      new Blob(
        [
          JSON.stringify({
            sectionQuestionId,
            isChunkUpload: false,
            isExplainedResponse : false,
            chunkType : 'NONE',
            isVideoType : true,
            sessionId: websocketSessionId,
            assessmentDriveSetId,
            userAssessmentDriveSetId,
          }),
        ],
        { type: 'application/json' }
      )
    );

    return this.saveChunkInDb(formData).pipe(retry(2));
  }

  callFallbackMongodbForAudioQuestion(
    requestPayload: UploadFile,
    sectionQuestionId: string,
    websocketSessionId: string | null,
    assessmentDriveSetId: string | null,
    userAssessmentDriveSetId: string | null
  ): Observable<any> {
    const formData = new FormData();

    formData.append('file', requestPayload as File);
    formData.append(
      'data',
      new Blob(
        [
          JSON.stringify({
            sectionQuestionId,
            isChunkUpload: false,
            chunkType : 'NONE',
            isExplainedResponse : false,
            isAudioType : true,
            sessionId: websocketSessionId,
            assessmentDriveSetId,
            userAssessmentDriveSetId,
          }),
        ],
        { type: 'application/json' }
      )
    );

    return this.saveChunkInDb(formData).pipe(retry(2));
  }

  callFallbackMongodbForExplainedVideoQuestion(
    requestPayload: UploadFile,
    sectionId: string,
    questionId:string,
    websocketSessionId: string | null,
    assessmentDriveSetId: string | null,
    userAssessmentDriveSetId: string | null
  ): Observable<any> {
    const formData = new FormData();

    formData.append('file', requestPayload as File);
    formData.append(
      'data',
      new Blob(
        [
          JSON.stringify({
            sectionId,
            questionId,
            chunkType : 'NONE',
            isChunkUpload: false,
            isExplainedResponse : true,
            sessionId: websocketSessionId,
            assessmentDriveSetId,
            userAssessmentDriveSetId,
          }),
        ],
        { type: 'application/json' }
      )
    );

    return this.saveChunkInDb(formData).pipe(retry(2));
  }

  callFallbackMongodbForVerifySurroundingVideo(
    requestPayload: UploadFile,
    sequence: number,
    chunkType: ChunkType,
    websocketSessionId: string | null,
    assessmentDriveSetId: string | null,
    userAssessmentDriveSetId: string | null
  ): Observable<any> {
    const formData = new FormData();

    formData.append('file', requestPayload as File);
    formData.append(
      'data',
      new Blob(
        [
          JSON.stringify({
            sequence,
            isChunkUpload: true,
            chunkType,
            sessionId: websocketSessionId,
            assessmentDriveSetId,
            userAssessmentDriveSetId,
          }),
        ],
        { type: 'application/json' }
      )
    );

    return this.saveChunkInDb(formData).pipe(retry(2));
  }

   generateResumeQuestion(driveId): Observable<any> {
    return this._http.get<any>(
      this.baseUrl + this.apiUrls.generateResumeQuestion + driveId
    );
  }

  scenarioBasedQuestion(driveId, questionType): Observable<any> {
    return this._http.get<any>(
      this.baseUrl + this.apiUrls.scenarioBasedQuestion + driveId + '/' + questionType
    );
  }
    // Reset Mobile Scanner Status for Bot
  resetMobileScannerStatusForBot(userAssessmentDriveSetId: any): Observable<any> {
    return this._http.put<ApiResponse<any>>(
       this.baseUrl + this.apiUrls.resetMobileScannerStatusForBot + '/' + userAssessmentDriveSetId,{}
    );
  }
 }
